import { useDispatch, useSelector } from "react-redux";

import { Button, Card } from "@chef/components";
import { Check } from "@chef/icons/small";
import { getDishCount } from "@chef/constants";
import {
  resetPersnalizedRecipesModal,
  selectPersonalizedRecipesModal,
} from "@chef/state-management";
import { Modal, PreselectedRecipesList } from "@chef/smart-components";

import { usePreviewRecipes } from "../hooks/usePreviewRecipes";

import { intl } from "./PersonalizedRecipesModal.Intl";

interface PersonalizedRecipesModalProps {
  portions: number;
  week: number;
  year: number;
}

interface CheckItemProps {
  children: React.ReactNode;
}

const CheckItem = ({ children }: CheckItemProps) => {
  return (
    <div className="flex items-center gap-2">
      <Check className="p-1 text-white circle bg-information" />
      {children}
    </div>
  );
};

export const PersonalizedRecipesModal = ({
  portions,
  week,
  year,
}: PersonalizedRecipesModalProps) => {
  const dispatch = useDispatch();

  const { open, conceptPreferenceIds, tastePreferenceIds } = useSelector(
    selectPersonalizedRecipesModal,
  );

  const recipes = usePreviewRecipes({
    week,
    year,
    portions,
    conceptPreferenceIds,
    tastePreferenceIds,
  });

  const onClose = () => {
    dispatch(resetPersnalizedRecipesModal());
  };

  const dishCount = getDishCount({ week, year, portions });

  return (
    <Modal
      name="personalized-recipes-modal"
      open={open}
      onClose={onClose}
      size="md"
    >
      <Card className="w-full min-h-screen text-center md:min-h-fit">
        <div className="mx-4 my-8 md:mx-8">
          <h2 className="flex flex-col items-center justify-center gap-1 my-2 text-xl">
            <strong>{intl.YOUR_PERSONALIZED_MENU}</strong>
          </h2>

          <div>
            {
              intl.DISHES_FROM_OUR_UPCOMING_WEEKS_MENU_THAT_MATCHES_YOUR_PREFERENCES
            }
          </div>

          <div className="flex justify-center gap-6 mt-4">
            <CheckItem>
              {intl.XX_DISHES_EACH_WEEK(getDishCount({ week, year, portions }))}
            </CheckItem>
            <CheckItem>
              <span className="text-sm">{intl.YOU_CHOOSE}</span>
            </CheckItem>
          </div>

          <hr className="my-6 border-t-1.5 border-grey-1" />

          <PreselectedRecipesList recipes={recipes} />

          <div className="mt-6">
            <h3 className="text-lg">
              <strong>{intl.CHOOSE_BETWEEN_N_DISHES(dishCount)}</strong>
            </h3>

            <div className="mt-2">
              {
                intl.SIGN_UP_AND_ENJOY_OUR_FULL_RANGE_OF_DELICIOUS_MEALS_EACH_WEEK
              }
            </div>
          </div>
        </div>
      </Card>

      <div className="sticky bottom-0 flex flex-col justify-center text-center bg-white">
        <div className="px-4 py-4 md:hidden md:px-20">
          <Button type="submit" onClick={onClose} primary full>
            {intl.CLOSE}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
